import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Save, Undo2, FlipHorizontal2, Wand2, Plus, Trash2, Repeat } from 'lucide-react';
import baseapi from '../../baseapi';

/** A count line. role 'toward': crossings along the arrow count toward the temple; 'away': along the arrow count away. */
export interface CountLine {
  id: string; label: string; toward_label: string; away_label: string;
  x1: number; y1: number; x2: number; y2: number; flip: boolean; role: 'toward' | 'away' | 'both';
  anchor?: 'center' | 'bottom' | 'top' | string;
}

const ROLE_COLOR: Record<string, string> = { toward: '#22C55E', away: '#F59E0B', both: '#38BDF8' };
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

/** Convert whatever a camera has into exactly two lines: toward (green) and away (orange). */
export function twoLines(lines: CountLine[]): CountLine[] {
  const toward = lines.find(l => l.role === 'toward');
  const away = lines.find(l => l.role === 'away');
  const base = lines.find(l => l.role === 'both') || lines[0];
  const t: CountLine = toward || (base
    ? { ...base, id: 'L-TOWARD', role: 'toward', label: base.label || 'Toward line', anchor: base.anchor || 'center' }
    : { id: 'L-TOWARD', label: 'Toward line', toward_label: 'Toward temple', away_label: 'Away from temple', x1: 0.1, y1: 0.5, x2: 0.9, y2: 0.5, flip: false, role: 'toward', anchor: 'center' });
  const a: CountLine = away || {
    ...t, id: 'L-AWAY', role: 'away', label: 'Away line',
    // parallel copy, shifted 8% towards the frame centre, arrow reversed
    y1: clamp01(t.y1 + (t.y1 < 0.5 ? 0.08 : -0.08)), y2: clamp01(t.y2 + (t.y2 < 0.5 ? 0.08 : -0.08)),
    x1: t.x1, x2: t.x2, flip: !t.flip, anchor: t.anchor || 'center',
  };
  return [t, a];
}

/** The lines exactly as the camera has them (a line without a role counts both directions).
 *  A camera with no line yet starts with the usual toward + away pair. */
export function editableLines(lines: CountLine[]): CountLine[] {
  if (!lines || lines.length === 0) return [];
  return lines.map(l => ({ ...l, role: l.role || 'both', anchor: l.anchor || 'center' }));
}

const ROLE_NAME: Record<string, string> = { toward: 'Toward', away: 'Away', both: 'Both ways' };
const NEXT_ROLE: Record<string, CountLine['role']> = { toward: 'away', away: 'both', both: 'toward' };
const NEXT_ANCHOR: Record<string, string> = { center: 'bottom', bottom: 'top', top: 'center' };

interface Props {
  cameraId: string;
  imgRef: React.RefObject<HTMLImageElement | null>;   // the MJPEG <img>; the overlay is fitted to its drawn area
  initialLines: CountLine[];
  onSaved: (lines: CountLine[]) => void;
  onCancel: () => void;
}

type Drag = { idx: number; part: 'p1' | 'p2' | 'mid'; ox: number; oy: number } | null;

export const LineEditor: React.FC<Props> = ({ cameraId, imgRef, initialLines, onSaved, onCancel }) => {
  const [lines, setLines] = useState<CountLine[]>(() => editableLines(initialLines));
  const [sel, setSel] = useState(0);
  const [drag, setDrag] = useState<Drag>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [box, setBox] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  // fit the overlay to the image's drawn area (object-contain letterboxing)
  useEffect(() => {
    const fit = () => {
      const img = imgRef.current; if (!img) return;
      const parent = img.parentElement; if (!parent) return;
      const pw = img.clientWidth, ph = img.clientHeight;
      const nw = img.naturalWidth || 16, nh = img.naturalHeight || 9;
      const s = Math.min(pw / nw, ph / nh);
      const w = nw * s, h = nh * s;
      const pr = parent.getBoundingClientRect(), ir = img.getBoundingClientRect();
      setBox({ left: ir.left - pr.left + (pw - w) / 2, top: ir.top - pr.top + (ph - h) / 2, width: w, height: h });
    };
    fit();
    const t = window.setInterval(fit, 500);
    window.addEventListener('resize', fit);
    return () => { window.clearInterval(t); window.removeEventListener('resize', fit); };
  }, [imgRef]);

  const toNorm = useCallback((e: React.PointerEvent) => {
    const r = svgRef.current?.getBoundingClientRect(); if (!r) return { x: 0, y: 0 };
    return { x: clamp01((e.clientX - r.left) / r.width), y: clamp01((e.clientY - r.top) / r.height) };
  }, []);
  const onDown = (idx: number, part: 'p1' | 'p2' | 'mid') => (e: React.PointerEvent) => {
    e.stopPropagation(); (e.target as Element).setPointerCapture?.(e.pointerId);
    const p = toNorm(e); const l = lines[idx]; setSel(idx);
    setDrag({ idx, part, ox: p.x - (part === 'p2' ? l.x2 : l.x1), oy: p.y - (part === 'p2' ? l.y2 : l.y1) });
  };
  const onMove = (e: React.PointerEvent) => {
    if (!drag) return; const p = toNorm(e);
    setLines(prev => prev.map((l, i) => {
      if (i !== drag.idx) return l;
      if (drag.part === 'p1') return { ...l, x1: p.x, y1: p.y };
      if (drag.part === 'p2') return { ...l, x2: p.x, y2: p.y };
      const nx1 = clamp01(p.x - drag.ox), ny1 = clamp01(p.y - drag.oy);
      return { ...l, x1: nx1, y1: ny1, x2: clamp01(nx1 + (l.x2 - l.x1)), y2: clamp01(ny1 + (l.y2 - l.y1)) };
    }));
  };
  const flip = () => setLines(ls => ls.map((l, i) => (i === sel ? { ...l, flip: !l.flip } : l)));
  const reset = () => { setLines(editableLines(initialLines)); setSel(0); setMsg(null); };
  // remove the selected line (allows 0 lines for holding area / zone-only cameras)
  const removeLine = () => {
    if (lines.length === 0) return;
    setLines(ls => {
      const next = ls.filter((_, i) => i !== sel);
      setSel(Math.max(0, Math.min(sel, next.length - 1)));
      return next;
    });
  };
  // add a line parallel to the selected one, or create an initial line if none exist
  const addLine = () => {
    if (lines.length === 0) {
      setLines([{
        id: 'L1', role: 'both', label: 'Line 1',
        toward_label: 'Toward', away_label: 'Away',
        x1: 0.1, y1: 0.5, x2: 0.9, y2: 0.5, flip: false, anchor: 'center',
      }]);
      setSel(0);
      return;
    }
    const base = lines[sel] || lines[0];
    // Queue-lane camera (lines Q1, Q2, ...) whose TOTAL line was removed: the new line IS the total line.
    // The lane counts and the "Single total line" row both depend on a line with exactly that id.
    if (lines.some(l => /^Q\d+$/.test(l.id)) && !lines.some(l => l.id === 'TOTAL')) {
      setLines(ls => [...ls, { ...base, id: 'TOTAL', role: 'both', label: 'Total line (all lanes)',
        x1: 0.1, y1: 0.5, x2: 0.9, y2: 0.5 }]);
      setSel(lines.length);
      return;
    }
    const role: CountLine['role'] = base.role === 'away' ? 'toward' : 'away';
    let id = role === 'away' ? 'L-AWAY' : 'L-TOWARD';
    for (let n = 2; lines.some(l => l.id === id); n++) id = `L-${n}`;
    const shift = base.y1 < 0.5 ? 0.08 : -0.08;
    setLines(ls => [...ls, { ...base, id, role, label: role === 'away' ? 'Away line' : 'Toward line', flip: !base.flip,
      anchor: base.anchor || 'center',
      y1: clamp01(base.y1 + shift), y2: clamp01(base.y2 + shift) }]);
    setSel(lines.length);
  };
  // what the selected line counts: toward only / away only / both directions
  const cycleRole = () => setLines(ls => ls.map((l, i) => (i === sel ? { ...l, role: NEXT_ROLE[l.role] || 'both' } : l)));
  // anchor point for the selected line: center (mid-body) -> bottom (feet) -> top (head)
  const cycleAnchor = () => setLines(ls => ls.map((l, i) => (i === sel ? { ...l, anchor: NEXT_ANCHOR[l.anchor || 'center'] || 'center' } : l)));
  const autoAway = () => {
    if (lines.length === 0) {
      setLines(twoLines([]));
      setSel(0);
      return;
    }
    setLines(ls => { const [t] = ls; return twoLines([{ ...t, role: 'both' }]); });
    setSel(0);
  };
  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const res = await baseapi.put(`/cameras/${encodeURIComponent(cameraId)}/lines`, lines);
      const saved: CountLine[] = (res.data?.lines || lines).map((l: any, i: number) => ({
        ...l,
        role: l.role || 'both',
        anchor: l.anchor || lines[i]?.anchor || 'center'
      }));
      setMsg(saved.length === 0
        ? 'Saved. No counting lines configured for this camera.'
        : `Saved. Counting now uses ${saved.length === 1 ? 'this line' : `these ${saved.length} lines`}; the line drawn in the video updates in a few seconds.`);
      onSaved(saved);
    } catch (e: any) { setMsg(`Save failed: ${e?.message || e}`); }
    finally { setSaving(false); }
  };

  return (
    <>
      <svg
        ref={svgRef} viewBox="0 0 1000 1000" preserveAspectRatio="none"
        className="absolute z-20 cursor-crosshair touch-none"
        style={box ? { left: box.left, top: box.top, width: box.width, height: box.height } : { inset: 0 }}
        onPointerMove={onMove} onPointerUp={() => setDrag(null)} onPointerLeave={() => setDrag(null)}
      >
        <defs>
          <marker id="le-arr-t" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0L10 5L0 10z" fill={ROLE_COLOR.toward} /></marker>
          <marker id="le-arr-a" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0L10 5L0 10z" fill={ROLE_COLOR.away} /></marker>
          <marker id="le-arr-b" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0L10 5L0 10z" fill={ROLE_COLOR.both} /></marker>
        </defs>
        {lines.length === 0 && (
          <text x="500" y="500" textAnchor="middle" dominantBaseline="middle" fill="#fff" fillOpacity="0.75" fontSize="28" fontWeight="600">
            No counting lines configured. Click &ldquo;Add line&rdquo; to create one.
          </text>
        )}
        {lines.map((l, i) => {
          const x1 = l.x1 * 1000, y1 = l.y1 * 1000, x2 = l.x2 * 1000, y2 = l.y2 * 1000;
          const mx = (x1 + x2) / 2, my = (y1 + y2) / 2, dx = x2 - x1, dy = y2 - y1, n = Math.hypot(dx, dy) || 1;
          let nx = dy / n, ny = -dx / n; if (l.flip) { nx = -nx; ny = -ny; }
          const col = ROLE_COLOR[l.role] || ROLE_COLOR.both, isSel = i === sel;
          return (
            <g key={l.id}>
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={col} strokeWidth={isSel ? 5 : 3} strokeDasharray={isSel ? undefined : '14 8'} vectorEffect="non-scaling-stroke" />
              <line x1={mx - nx * 30} y1={my - ny * 30} x2={mx + nx * 70} y2={my + ny * 70} stroke={col} strokeWidth={3} vectorEffect="non-scaling-stroke" markerEnd={`url(#${l.role === 'away' ? 'le-arr-a' : l.role === 'both' ? 'le-arr-b' : 'le-arr-t'})`} />
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="transparent" strokeWidth={34} vectorEffect="non-scaling-stroke" style={{ cursor: 'move' }} onPointerDown={onDown(i, 'mid')} />
              <circle cx={x1} cy={y1} r={13} fill={col} stroke="#fff" strokeWidth={2} vectorEffect="non-scaling-stroke" style={{ cursor: 'grab' }} onPointerDown={onDown(i, 'p1')} />
              <circle cx={x2} cy={y2} r={13} fill={col} stroke="#fff" strokeWidth={2} vectorEffect="non-scaling-stroke" style={{ cursor: 'grab' }} onPointerDown={onDown(i, 'p2')} />
              <text x={Math.min(x1, x2) + 10} y={Math.min(y1, y2) - 14} fontSize={30} fill="#fff" stroke="#000" strokeWidth={5} paintOrder="stroke" fontWeight={700}>
                {l.role === 'both' ? 'BOTH WAYS · arrow = toward direction' : `${l.role === 'away' ? 'AWAY' : 'TOWARD'} · arrow = counted direction`}
                {` [anchor: ${l.anchor || 'center'}]`}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="absolute left-0 right-0 bottom-0 z-30 bg-[#0c0e15]/90 backdrop-blur-xs border-t border-white/10 px-2.5 py-1.5 flex flex-wrap items-center gap-1.5 text-[10px]">
        {lines.map((l, i) => (
          <button key={l.id} onClick={() => setSel(i)} className={`px-2 py-0.5 rounded font-bold border cursor-pointer ${i === sel ? 'text-black' : 'text-white/80 border-white/20'}`}
            style={i === sel ? { background: ROLE_COLOR[l.role], borderColor: ROLE_COLOR[l.role] } : {}}>
            {ROLE_NAME[l.role] || 'Both ways'} line{lines.filter(x => x.role === l.role).length > 1 ? ` ${i + 1}` : ''}
          </button>
        ))}
        <button onClick={flip} disabled={lines.length === 0} className="px-2 py-0.5 rounded border border-white/20 text-white/80 hover:text-white flex items-center gap-1 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"><FlipHorizontal2 className="w-3 h-3" />Flip arrow</button>
        <button onClick={cycleRole} disabled={lines.length === 0} title="Change what the selected line counts: toward only, away only, or both directions" className="px-2 py-0.5 rounded border border-white/20 text-white/80 hover:text-white flex items-center gap-1 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"><Repeat className="w-3 h-3" />Counts: {lines.length ? (ROLE_NAME[lines[sel]?.role] || 'Both ways') : '—'}</button>
        <button onClick={cycleAnchor} disabled={lines.length === 0} title="Toggle anchor point for this line: center (mid-body), bottom (feet / ground contact), or top (head)" className="px-2 py-0.5 rounded border border-white/20 text-white/80 hover:text-white flex items-center gap-1 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">Anchor: {lines.length ? (lines[sel]?.anchor || 'center') : '—'}</button>
        <button onClick={addLine} title="Add another line next to the selected one" className="px-2 py-0.5 rounded border border-white/20 text-white/80 hover:text-white flex items-center gap-1 cursor-pointer"><Plus className="w-3 h-3" />Add line</button>
        <button onClick={removeLine} disabled={lines.length === 0} title={lines.length === 0 ? 'No lines to remove' : 'Remove the selected line'} className="px-2 py-0.5 rounded border border-rose-400/40 text-rose-300 hover:text-rose-200 flex items-center gap-1 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 className="w-3 h-3" />Remove line</button>
        <button onClick={autoAway} title="Place the away line parallel to the toward line with the opposite arrow" className="px-2 py-0.5 rounded border border-white/20 text-white/80 hover:text-white flex items-center gap-1 cursor-pointer"><Wand2 className="w-3 h-3" />Auto away line</button>
        <button onClick={reset} className="px-2 py-0.5 rounded border border-white/20 text-white/80 hover:text-white flex items-center gap-1 cursor-pointer"><Undo2 className="w-3 h-3" />Revert</button>
        <span className="flex-1 text-white/50 truncate">Drag the handles across the passage. Green = toward, orange = away, blue = both ways. The arrow is the direction of travel the line counts. Nothing changes until you save.</span>
        <button onClick={onCancel} className="px-2 py-0.5 rounded border border-white/20 text-white/80 hover:text-white cursor-pointer">Close</button>
        <button onClick={save} disabled={saving} className="px-2.5 py-0.5 rounded bg-emerald-500 text-black font-bold flex items-center gap-1 cursor-pointer disabled:opacity-50"><Save className="w-3 h-3" />{saving ? 'Saving…' : 'Save lines'}</button>
        {msg && <span className="w-full text-sky-300">{msg}</span>}
      </div>
    </>
  );
};
